import React, { useEffect } from 'react'
import { X, Lock } from 'lucide-react'
import { useCheckout } from '../../hooks/useCheckout'
import { TIERS } from '../../lib/stripe'
import './PaywallModal.css'

// ─────────────────────────────────────────────────────────────────────────────
// PaywallModal
// Shown when an unsubscribed user clicks any Foundation lesson tile in
// the universe map's side panel.
//
// Behaviour:
//   - status === 'active' is the only state that grants access; everything
//     else (inactive, paused, cancelled, tier 0) triggers this modal
//   - lesson title is shown so the user knows what they're trying to open
//   - subscribe CTA goes through the standard Foundation checkout flow
//   - secondary CTA closes the modal (back to map)
//   - ESC and overlay click also close
// ─────────────────────────────────────────────────────────────────────────────

export function PaywallModal({ isOpen, onClose, lessonTitle, subStatus }) {
  const { startCheckout, loading: checkoutLoading } = useCheckout()

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return
    function handleKey(e) { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [isOpen, onClose])

  // Lock background scroll while open
  useEffect(() => {
    if (!isOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [isOpen])

  if (!isOpen) return null

  // Headline copy adapts to the user's current state
  const headline =
    subStatus === 'paused'    ? 'Resume your subscription to continue'
  : subStatus === 'cancelled' ? 'Reactivate your subscription to continue'
  : 'Subscribe to access this lesson'

  const eyebrow =
    subStatus === 'paused'    ? 'Subscription paused'
  : subStatus === 'cancelled' ? 'Subscription cancelled'
  : 'Locked'

  return (
    <>
      <div className="paywall-overlay" onClick={onClose} aria-hidden="true" />
      <div className="paywall-modal" role="dialog" aria-modal="true" aria-labelledby="paywall-title">

        <button className="paywall-modal__close" onClick={onClose} aria-label="Close">
          <X size={18} />
        </button>

        <div className="paywall-modal__icon">
          <Lock size={22} />
        </div>

        <div className="paywall-modal__eyebrow mono">{eyebrow}</div>
        <h2 className="paywall-modal__title" id="paywall-title">{headline}</h2>

        {lessonTitle && (
          <div className="paywall-modal__lesson">
            <span className="paywall-modal__lesson-label mono">You tried to open</span>
            <span className="paywall-modal__lesson-title">{lessonTitle}</span>
          </div>
        )}

        <p className="paywall-modal__body">
          TechBench is paid from the first lesson. Foundation gives you all three tracks —
          IT Foundation, Human Skills, and AI Foundations — plus the capstone and your first certificate.
        </p>

        <div className="paywall-modal__price-row">
          <div className="paywall-modal__price">
            <span className="paywall-modal__price-value">{TIERS[1].label}</span>
            <span className="paywall-modal__price-note mono">{TIERS[1].firstMonthLabel} your first month</span>
          </div>
        </div>

        <div className="paywall-modal__waiver mono">
          By subscribing, you agree to the{' '}
          <a href="https://techbench.it/terms" target="_blank" rel="noreferrer">Terms</a>,{' '}
          <a href="https://techbench.it/user-agreement" target="_blank" rel="noreferrer">User Agreement</a>, and{' '}
          <a href="https://techbench.it/refund-policy" target="_blank" rel="noreferrer">Refund Policy</a>.
          You consent to immediate content delivery and waive your 14-day cooling-off right.
        </div>

        <button
          className="paywall-modal__cta"
          onClick={() => startCheckout({ type: 'subscription_t1' })}
          disabled={checkoutLoading}
        >
          {checkoutLoading ? 'Redirecting…' : `Subscribe and start — ${TIERS[1].firstMonthLabel}`}
        </button>

        <button className="paywall-modal__secondary" onClick={onClose}>
          Back to the map
        </button>

        <div className="paywall-modal__fine mono">
          Cancel anytime. Pause and resume. One-way progression —{' '}
          <a href="https://techbench.it/how-it-works" target="_blank" rel="noreferrer">how it works</a>.
        </div>
      </div>
    </>
  )
}
